import Link from 'next/link'
import { ReactNode, useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import styled from 'styled-components'
import type { TeamBadge } from './api'
import { isBadge } from './badges'

export { isBadge }

/*
 * Person ticks (Bez, 2026-10-04): who a PROFILE is, never what a token is.
 *   gold shield + check   Official CADINU account (set by hand in the admin, our own accounts only)
 *   blue shield + person  Token team: "<Role> · <SYMBOL>", confirmed by the wallet's signature on approved project info
 * Design choice: people get a SHIELD (a badge worn by someone); tokens keep the round scalloped SEAL (blue "Verified token team", gold
 * "Verified by CADINU team"). Shape tells person from token, colour and glyph tell the two person ticks apart: gold + check vs
 * an indigo-blue (#5b82ff, not the token seal's sky blue) + a person. Lit from the top left like the token seals, so the family
 * still looks like one system on a dark page.
 *
 * ONE shield per person (Bez, 2026-10-04): the gold official shield wins over the blue token-team one. A profile that is both shows the
 * gold shield, and its "<Role> · <SYMBOL>" chips as text only; otherwise the first team chip carries the one blue shield. The shield
 * scales with the name beside it (about 20 px in rows and lists, up to ~30 px beside a big heading).
 *
 * Two modes:
 *   interactive  each tick is its own target: the gold one a button, a team chip a link to /token/<address>; a tooltip opens on
 *                hover, keyboard focus or tap, Escape closes it.
 *   inline       inside a row that is already one link (/u): the ticks are pictures (role img) and their meaning joins the row's
 *                name; one tooltip opens on hover and when the row link gets keyboard focus. No link inside a link.
 */

const useIsoLayoutEffect = typeof window === 'undefined' ? useEffect : useLayoutEffect

export type PersonTickKind = 'official' | 'team'
const SHIELD = 'M12 2.3 19.4 5c.6.2 1 .8 1 1.4v5.1c0 4.8-3.4 8.8-8.4 10.3C7 20.3 3.6 16.3 3.6 11.5V6.4c0-.6.4-1.2 1-1.4z'
const TONES: Record<PersonTickKind, { hi: string; mid: string; lo: string; glyph: string; glow: string; ring: string }> = {
  // dark check on gold: white on gold would be under 2:1
  official: { hi: '#fff4c4', mid: '#f3b63b', lo: '#a86a09', glyph: '#2a1800', glow: 'rgba(243, 182, 59, 0.55)', ring: 'rgba(255, 241, 204, 0.75)' },
  // white person on indigo-blue: >= 3:1 on every part of the face
  team: { hi: '#d4e0ff', mid: '#5b82ff', lo: '#2a3fc0', glyph: '#ffffff', glow: 'rgba(91, 130, 255, 0.55)', ring: 'rgba(214, 224, 255, 0.6)' },
}
export const OFFICIAL_TEXT = 'Official CADINU account'
export const teamText = (b: TeamBadge) => `${b.roleLabel} of ${b.symbol || shortToken(b.token)}`
export const shortToken = (a: string) => (a && a.length > 12 ? `${a.slice(0, 6)}…${a.slice(-4)}` : a)
export const chipText = (b: TeamBadge) => `${b.roleLabel} · ${b.symbol || shortToken(b.token)}`

/** The shield alone, size in px (16 to 22 on the page). */
export function PersonShield({ kind, size = 18 }: { kind: PersonTickKind; size?: number }) {
  const t = TONES[kind]
  const uid = useId().replace(/:/g, '')
  const g = `ps-${kind}-${uid}`
  const s = `ps-sheen-${uid}`
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      aria-hidden="true"
      focusable="false"
      data-person-tick={kind}
      style={{ flex: 'none', overflow: 'visible', filter: `drop-shadow(0 0 3.5px ${t.glow}) drop-shadow(0 1px 1px rgba(0,0,0,0.5))` }}
    >
      <defs>
        <radialGradient id={g} cx="32%" cy="22%" r="85%">
          <stop offset="0%" stopColor={t.hi} />
          <stop offset="50%" stopColor={t.mid} />
          <stop offset="100%" stopColor={t.lo} />
        </radialGradient>
        <linearGradient id={s} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#fff" stopOpacity="0.38" />
          <stop offset="45%" stopColor="#fff" stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={SHIELD} fill={`url(#${g})`} stroke={t.ring} strokeWidth="0.7" strokeLinejoin="round" />
      <path d={SHIELD} fill={`url(#${s})`} />
      {kind === 'official' ? (
        <path d="M8.1 11.9 10.8 14.6 16 9.2" fill="none" stroke={t.glyph} strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
      ) : (
        <g fill={t.glyph}>
          <circle cx="12" cy="9.3" r="2.6" />
          <path d="M7.4 16.6c.6-2.5 2.4-3.8 4.6-3.8s4 1.3 4.6 3.8c-1.3 1.1-2.9 1.9-4.6 2.3-1.7-.4-3.3-1.2-4.6-2.3z" />
        </g>
      )}
    </svg>
  )
}

/* ───────────────────────── tooltip ───────────────────────── */
const Bubble = styled.div<{ $kind: PersonTickKind | 'mixed' }>`
  position: fixed;
  z-index: 1500; /* above the header wallet panel (1401) */
  max-width: min(260px, calc(100vw - 24px));
  padding: 8px 12px;
  border-radius: 12px;
  font-size: 13px;
  line-height: 18px;
  font-weight: 600;
  color: #eef2ff;
  background: linear-gradient(180deg, #161c3a 0%, #0b1026 100%);
  border: 1px solid ${({ $kind }) => ($kind === 'official' ? 'rgba(243, 182, 59, 0.5)' : $kind === 'team' ? 'rgba(124, 156, 255, 0.55)' : 'rgba(201, 214, 255, 0.35)')};
  box-shadow: 0 12px 30px rgba(0, 0, 0, 0.55), 0 0 22px ${({ $kind }) => ($kind === 'official' ? 'rgba(243, 182, 59, 0.14)' : 'rgba(91, 130, 255, 0.16)')};
  pointer-events: none;
  text-align: left;
  white-space: normal;
  span { display: block; }
  span + span { margin-top: 4px; }
  small { display: block; font-size: 12px; line-height: 16px; font-weight: 500; color: #b9c3e0; }
`

function useTip() {
  const [open, setOpen] = useState(false)
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null)
  const anchor = useRef<HTMLElement | null>(null)
  const bubble = useRef<HTMLDivElement>(null)
  const timer = useRef<ReturnType<typeof setTimeout>>()
  const place = useCallback(() => {
    const a = anchor.current?.getBoundingClientRect()
    if (!a) return
    const w = bubble.current?.offsetWidth ?? 220
    const h = bubble.current?.offsetHeight ?? 40
    const left = Math.max(12, Math.min(a.left + a.width / 2 - w / 2, window.innerWidth - w - 12))
    const above = a.top - h - 8
    setPos({ top: above > 8 ? above : a.bottom + 8, left })
  }, [])
  useIsoLayoutEffect(() => {
    if (!open) return undefined
    place()
    const on = () => place()
    window.addEventListener('scroll', on, true)
    window.addEventListener('resize', on)
    return () => {
      window.removeEventListener('scroll', on, true)
      window.removeEventListener('resize', on)
    }
  }, [open, place])
  useEffect(() => () => clearTimeout(timer.current), [])
  const show = useCallback(() => {
    clearTimeout(timer.current)
    setOpen(true)
  }, [])
  const hide = useCallback((later?: boolean) => {
    clearTimeout(timer.current)
    if (later) timer.current = setTimeout(() => setOpen(false), 120)
    else setOpen(false)
  }, [])
  return { open, pos, anchor, bubble, show, hide, setOpen }
}

function TipBubble({ tip, id, kind, children, testId }: { tip: ReturnType<typeof useTip>; id: string; kind: PersonTickKind | 'mixed'; children: ReactNode; testId: string }) {
  if (!tip.open || typeof document === 'undefined') return null
  return createPortal(
    <Bubble ref={tip.bubble} id={id} role="tooltip" $kind={kind} data-testid={testId} style={tip.pos ? { top: tip.pos.top, left: tip.pos.left } : { top: -9999, left: -9999 }}>
      {children}
    </Bubble>,
    document.body,
  )
}

const keyboardFocus = (el: Element) => {
  try {
    return el.matches(':focus-visible')
  } catch {
    return true
  }
}

/* ───────────────────────── interactive ticks ───────────────────────── */
const TickBtn = styled.button<{ $box: number; $size: number; $scale: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: none;
  font-size: inherit;
  width: ${({ $box, $scale }) => ($scale ? `max(${$box}px, 1.05em)` : `${$box}px`)};
  height: ${({ $box, $scale }) => ($scale ? `max(${$box}px, 1.05em)` : `${$box}px`)};
  ${({ $scale, $size }) => ($scale ? `svg { width: max(${$size}px, 0.78em); height: max(${$size}px, 0.78em); }` : '')}
  padding: 0;
  border: 0;
  border-radius: 10px;
  background: transparent;
  cursor: help;
  vertical-align: middle;
  &:hover { background: rgba(255, 236, 190, 0.08); }
  &:focus-visible { outline: 2px solid #f3b63b; outline-offset: 1px; }
`
const ChipBase = `
  display: inline-flex;
  align-items: center;
  gap: 4px;
  flex: none;
  max-width: 100%;
  padding: 0 9px 0 3px;
  &[data-shield='0'] { padding-left: 9px; }
  border-radius: 999px;
  font-weight: 700;
  white-space: nowrap;
  color: #d6e0ff;
  text-decoration: none;
  letter-spacing: 0.005em;
  background: linear-gradient(180deg, rgba(91, 130, 255, 0.24), rgba(42, 63, 192, 0.16));
  border: 1px solid rgba(124, 156, 255, 0.5);
  box-shadow: 0 0 14px -6px rgba(91, 130, 255, 0.75), inset 0 1px 0 rgba(255, 255, 255, 0.08);
  b { color: #fff; font-weight: 800; }
  .sep { opacity: 0.6; }
`
const ChipLink = styled(Link)<{ $h: number }>`
  ${ChipBase}
  height: ${({ $h }) => $h}px;
  font-size: ${({ $h }) => ($h >= 28 ? 13 : 12)}px;
  line-height: 16px;
  &:hover { border-color: rgba(170, 192, 255, 0.85); color: #fff; }
  &:focus-visible { outline: 2px solid #8fa8ff; outline-offset: 2px; }
`
const ChipSpan = styled.span<{ $h: number }>`
  ${ChipBase}
  height: ${({ $h }) => $h}px;
  font-size: 12px;
  line-height: 16px;
`

/** `scale`: the shield grows with the font size of the heading it sits in (never below `size` / `box` px). */
export function OfficialTick({ size = 20, box = 28, scale = false }: { size?: number; box?: number; scale?: boolean }) {
  const tip = useTip()
  const id = `pt-official-${useId().replace(/:/g, '')}`
  return (
    <>
      <TickBtn
        ref={(el) => { tip.anchor.current = el }}
        type="button"
        $box={box}
        $size={size}
        $scale={scale}
        aria-label={OFFICIAL_TEXT}
        aria-describedby={tip.open ? id : undefined}
        data-testid="pt-official"
        onClick={() => tip.setOpen((o) => !o)}
        onPointerEnter={(e) => e.pointerType === 'mouse' && tip.show()}
        onPointerLeave={(e) => e.pointerType === 'mouse' && tip.hide(true)}
        onFocus={(e) => keyboardFocus(e.currentTarget) && tip.show()}
        onBlur={() => tip.hide()}
        onKeyDown={(e) => e.key === 'Escape' && tip.hide()}
      >
        <PersonShield kind="official" size={size} />
      </TickBtn>
      <TipBubble tip={tip} id={id} kind="official" testId="pt-official-tip">
        <span>{OFFICIAL_TEXT}</span>
        <small>Run by the CADINU team. Only CADINU&apos;s own accounts carry this tick.</small>
      </TipBubble>
    </>
  )
}

/** `shield={false}`: the chip is text only, because the profile already shows its one shield (the gold official one, or a first chip). */
export function TeamChip({ badge, size = 18, h = 26, shield = true }: { badge: TeamBadge; size?: number; h?: number; shield?: boolean }) {
  const tip = useTip()
  const id = `pt-team-${useId().replace(/:/g, '')}`
  const sym = badge.symbol || shortToken(badge.token)
  return (
    <>
      <ChipLink
        ref={(el: HTMLAnchorElement | null) => { tip.anchor.current = el }}
        href={`/token/${badge.token}`}
        $h={h}
        aria-label={`Token team: ${teamText(badge)}. Open the ${sym} token page`}
        aria-describedby={tip.open ? id : undefined}
        data-testid="pt-team"
        data-token={badge.token}
        data-shield={shield ? '1' : '0'}
        onPointerEnter={(e) => e.pointerType === 'mouse' && tip.show()}
        onPointerLeave={(e) => e.pointerType === 'mouse' && tip.hide(true)}
        onFocus={(e) => keyboardFocus(e.currentTarget) && tip.show()}
        onBlur={() => tip.hide()}
        onKeyDown={(e) => e.key === 'Escape' && tip.hide()}
      >
        {shield && <PersonShield kind="team" size={size} />}
        <span>{badge.roleLabel}</span>
        <span className="sep" aria-hidden="true">·</span>
        <b>{sym}</b>
      </ChipLink>
      <TipBubble tip={tip} id={id} kind="team" testId="pt-team-tip">
        <span>Token team: {teamText(badge)}</span>
        <small>The wallet confirmed it by signature and CADINU approved the project&apos;s info.</small>
      </TipBubble>
    </>
  )
}

const Row = styled.span<{ $wrap: boolean }>`
  display: inline-flex;
  align-items: center;
  flex-wrap: ${({ $wrap }) => ($wrap ? 'wrap' : 'nowrap')};
  gap: 4px 6px;
  min-width: 0;
  max-width: 100%;
  vertical-align: middle;
`

/** Which one shield a profile shows: gold official wins over blue token team; null for neither. */
export function shieldOf(official?: boolean, teams?: TeamBadge[] | null): PersonTickKind | null {
  if (official) return 'official'
  return (teams ?? []).some(isBadge) ? 'team' : null
}

/** What a profile shows beside its name: one shield (gold official, else the first team chip's blue one), then its token-team chips (at most two). */
export default function PersonTicks({
  official,
  teams,
  size = 20,
  box = 28,
  chipHeight = 26,
  wrap = true,
  testId = 'person-ticks',
}: {
  official?: boolean
  teams?: TeamBadge[] | null
  size?: number
  box?: number
  chipHeight?: number
  wrap?: boolean
  testId?: string
}) {
  const list = (teams ?? []).filter(isBadge).slice(0, 2)
  if (!official && !list.length) return null
  return (
    <Row $wrap={wrap} data-testid={testId}>
      {official && <OfficialTick size={size} box={box} />}
      {list.map((b, i) => <TeamChip key={`${b.token}:${b.role}`} badge={b} size={Math.min(size, 20)} h={chipHeight} shield={!official && i === 0} />)}
    </Row>
  )
}

/* ───────────────────────── inline (inside a row link) ───────────────────────── */
const Pic = styled.span`
  display: inline-flex;
  align-items: center;
  flex: none;
`

/** The words a screen reader hears for a profile's ticks; rows add them to their link's name. */
export function ticksLabel(official?: boolean, teams?: TeamBadge[] | null): string {
  const parts = [official ? OFFICIAL_TEXT : '', ...(teams ?? []).filter(isBadge).slice(0, 2).map((b) => `token team: ${teamText(b)}`)].filter(Boolean)
  return parts.join(', ')
}

export function InlineTicks({ official, teams, size = 20, chip = true, testId = 'person-ticks' }: { official?: boolean; teams?: TeamBadge[] | null; size?: number; chip?: boolean; testId?: string }) {
  const list = (teams ?? []).filter(isBadge).slice(0, 2)
  const tip = useTip()
  const id = `pt-inline-${useId().replace(/:/g, '')}`
  const wrapRef = useRef<HTMLSpanElement | null>(null)
  // the row link's keyboard focus opens the same tooltip
  useEffect(() => {
    const link = wrapRef.current?.closest('a')
    if (!link) return undefined
    const on = () => keyboardFocus(link) && tip.show()
    const off = () => tip.hide()
    const esc = (e: KeyboardEvent) => e.key === 'Escape' && tip.hide()
    link.addEventListener('focus', on)
    link.addEventListener('blur', off)
    link.addEventListener('keydown', esc)
    return () => {
      link.removeEventListener('focus', on)
      link.removeEventListener('blur', off)
      link.removeEventListener('keydown', esc)
    }
  }, [tip.show, tip.hide]) // eslint-disable-line react-hooks/exhaustive-deps
  if (!official && !list.length) return null
  const kind: PersonTickKind | 'mixed' = official && list.length ? 'mixed' : official ? 'official' : 'team'
  return (
    <>
      <Row
        $wrap /* two chips in a narrow row (a phone's board) wrap inside the cell instead of sticking out */
        ref={(el: HTMLSpanElement | null) => {
          wrapRef.current = el
          tip.anchor.current = el
        }}
        data-testid={testId}
        aria-describedby={tip.open ? id : undefined}
        onPointerEnter={(e) => e.pointerType === 'mouse' && tip.show()}
        onPointerLeave={(e) => e.pointerType === 'mouse' && tip.hide(true)}
      >
        {official && (
          <Pic role="img" aria-label={OFFICIAL_TEXT} data-testid="pt-official">
            <PersonShield kind="official" size={size} />
          </Pic>
        )}
        {list.map((b, i) => {
          const shield = !official && i === 0 // one shield per person: gold wins, else the first chip's blue one
          return chip ? (
            <ChipSpan key={`${b.token}:${b.role}`} $h={24} role="img" aria-label={`Token team: ${teamText(b)}`} data-testid="pt-team" data-token={b.token} data-shield={shield ? '1' : '0'}>
              {shield && <PersonShield kind="team" size={Math.min(size, 18)} />}
              <span className="chip-words">
                <span>{b.roleLabel}</span>
                <span className="sep"> · </span>
                <b>{b.symbol || shortToken(b.token)}</b>
              </span>
            </ChipSpan>
          ) : shield ? (
            <Pic key={`${b.token}:${b.role}`} role="img" aria-label={`Token team: ${teamText(b)}`} data-testid="pt-team" data-token={b.token}>
              <PersonShield kind="team" size={size} />
            </Pic>
          ) : null
        })}
      </Row>
      <TipBubble tip={tip} id={id} kind={kind} testId="pt-inline-tip">
        {official && <span>{OFFICIAL_TEXT}</span>}
        {list.map((b) => <span key={`${b.token}:${b.role}`}>Token team: {teamText(b)}</span>)}
      </TipBubble>
    </>
  )
}
